import {
  asCarePlanId,
  asMemberId,
  type CarePlanId,
  type CarePlanStatus,
  type ConditionId,
  type MemberId,
} from "@repo/domain";
import { z } from "zod";
import { caseIdSchema } from "./case.schemas";
import { conditionIdSchema } from "./condition.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { patientRefSchema } from "./patient.schemas";

// === CarePlan Status / Intent ===

/**
 * Exactly the states a care plan can be in — the domain's own list, not FHIR's.
 *
 * This used to carry `entered-in-error` and `unknown` as well. The plan has no
 * such states, so the surface was accepting a status the domain could not hold
 * and something downstream had to notice. The two assertions below make that a
 * build error instead of a runtime one: `satisfies` refuses a status the domain
 * does not have, and `_noneMissing` refuses one it has that this omits.
 */
export const carePlanStatusSchema = z.enum([
  "draft",
  "active",
  "on-hold",
  "revoked",
  "completed",
] as const satisfies readonly CarePlanStatus[]);

/**
 * Fails to compile if the domain gains a status this enum omits. Must be a
 * VALUE, not a type alias: an alias that resolves to `never` is simply an alias
 * nobody read, and asserts nothing.
 */
export const carePlanStatusesAreComplete: Exclude<CarePlanStatus, z.infer<typeof carePlanStatusSchema>> extends never
  ? true
  : never = true;

/** Omit the field entirely for a goal that is not ranked. */
const goalPrioritySchema = z.enum(["high-priority", "medium-priority", "low-priority"]);

// === Goal Target ===

/**
 * What a goal aims at: a measurable quantity, or an outcome in words.
 *
 * FLAT, not `{measure, detail: {...}}`. That nesting is `Goal.target.detail[x]`
 * — FHIR's shape, and it stops at the mapper. "A quantity or some text" is one
 * choice, not a thing wrapped in another thing, and spelling it FHIR's way here
 * forced every caller and both handlers through a translation step.
 */
const goalTargetSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("quantity"),
    /** What is tracked — free text. FHIR requires it alongside a detail (invariant gol-1). */
    measure: z.string().min(1),
    /** FHIR date (YYYY-MM-DD) deadline. */
    due: z.string().optional(),
    comparator: z.enum(["<", "<=", ">=", ">"]).optional(),
    value: z.number(),
    unit: z.string().optional(),
  }),
  z.object({
    kind: z.literal("text"),
    measure: z.string().min(1),
    due: z.string().optional(),
    text: z.string().min(1),
  }),
]);

// === CarePlan Input Schemas ===

export const createCarePlanInputSchema = z.object({
  id: newEntityIdSchema,
  episodeOfCare: z.object({ resourceType: z.literal("EpisodeOfCare"), id: caseIdSchema }),
  patient: patientRefSchema,
  title: z.string().optional(),
  description: z.string().optional(),
});
export type CreateCarePlanInput = z.infer<typeof createCarePlanInputSchema>;

export const carePlanIdSchema: z.ZodType<CarePlanId, z.ZodTypeDef, string> = z.string().min(1).transform(asCarePlanId);
export const goalIdSchema: z.ZodType<MemberId, z.ZodTypeDef, string> = z.string().min(1).transform(asMemberId);

export const carePlanIdInputSchema = z.object({
  carePlanId: carePlanIdSchema,
});
export type CarePlanIdInput = z.infer<typeof carePlanIdInputSchema>;

export const reviseCarePlanInputSchema = z.object({
  carePlanId: carePlanIdSchema,
  title: z.string().optional(),
  description: z.string().optional(),
  addresses: z.array(conditionIdSchema).optional(),
});
export type ReviseCarePlanInput = z.infer<typeof reviseCarePlanInputSchema>;

export const addGoalInputSchema = z.object({
  carePlanId: carePlanIdSchema,
  description: z.string().min(1),
  /** Omitted ⇒ the goal is not ranked. */
  priority: goalPrioritySchema.optional(),
  /** FHIR date (YYYY-MM-DD) — when goal pursuit begins. */
  startDate: z.string().optional(),
  /** Condition ids this goal targets — a subset of the plan's addresses[]. */
  addresses: z.array(conditionIdSchema).optional(),
  /** Measurable target outcome. */
  target: goalTargetSchema.optional(),
});
export type AddGoalInput = z.infer<typeof addGoalInputSchema>;

type ReviseGoalInputRaw = Omit<ReviseGoalInput, "carePlanId" | "goalId" | "addresses"> & {
  carePlanId: string;
  goalId: string;
  addresses?: string[];
};

export interface ReviseGoalInput {
  carePlanId: CarePlanId;
  goalId: MemberId;
  description?: string;
  priority?: z.infer<typeof goalPrioritySchema> | null;
  startDate?: string;
  addresses?: ConditionId[];
  target?: z.infer<typeof goalTargetSchema> | null;
}

export const reviseGoalInputSchema: z.ZodType<ReviseGoalInput, z.ZodTypeDef, ReviseGoalInputRaw> = z.object({
  carePlanId: carePlanIdSchema,
  goalId: goalIdSchema,
  description: z.string().min(1).optional(),
  /** `null` un-ranks the goal; omitted leaves it unchanged. */
  priority: goalPrioritySchema.nullable().optional(),
  startDate: z.string().optional(),
  addresses: z.array(conditionIdSchema).optional(),
  /** `null` clears the target; omitted leaves it unchanged. */
  target: goalTargetSchema.nullable().optional(),
});
